import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  GraduationCap,
  RefreshCw,
  Download,
  UserPlus,
  AlertCircle,
  CheckCircle2,
  Clock,
  Calendar,
  Loader2,
  Award,
  RotateCcw,
  BookOpen,
  AlertTriangle,
  Pencil,
  FastForward,
  Plus,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormat';
import { HelpConnectAccessButton } from './HelpConnectAccessButton';
import { HelpConnectViewPortalButton } from './HelpConnectViewPortalButton';
import { RemoveCourseButton } from './RemoveCourseButton';
import { RestoreCourseButton } from './RestoreCourseButton';
import { HelpConnectAccessStatus } from './HelpConnectAccessStatus';
import { HelpConnectCertificatesButton } from './HelpConnectCertificatesButton';
import { StaffPathwaysTracker } from './StaffPathwaysTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge styling mappings
const ENROLLMENT_STATUS_STYLES = {
  not_enrolled: { label: 'Not Enrolled', variant: 'secondary', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  enrolled: { label: 'Enrolled', variant: 'default', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  suspended: { label: 'Suspended', variant: 'destructive', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  removed: { label: 'Removed', variant: 'destructive', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  left: { label: 'Left', variant: 'secondary', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  enrollment_failed: { label: 'Enrollment Failed', variant: 'destructive', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  retrying: { label: 'Retry in Progress', variant: 'secondary', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 animate-pulse' }
};

const PROGRESS_STATUS_STYLES = {
  not_started: { label: 'Not Started', variant: 'secondary', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  in_progress: { label: 'In Progress', variant: 'default', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  completed: { label: 'Completed', variant: 'default', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  failed: { label: 'Failed', variant: 'destructive', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  'completed - employed/retention': { label: 'Completed - Employed/Retention', variant: 'default', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' }
};

const CERTIFICATE_STATUS_STYLES = {
  not_eligible: { label: 'Not Eligible', variant: 'secondary', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  pending: { label: 'Pending', variant: 'secondary', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  available: { label: 'Available', variant: 'default', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  downloaded: { label: 'Downloaded', variant: 'default', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  error: { label: 'Error', variant: 'destructive', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
};

const CHANNEL_LABELS = { sms: 'text', email: 'email' };

// Tell staff exactly what HELPConnect access was sent after an enrollment
const showAccessResult = (access) => {
  if (!access) return;
  const delivery = access.delivery || {};
  const sent = Object.keys(delivery).filter((k) => delivery[k] === 'sent').map((k) => CHANNEL_LABELS[k] || k);
  const what = access.purpose === 'course_added' ? '"New course added" note' : 'HELPConnect setup link';
  if (sent.length) toast.success(`${what} sent by ${sent.join(' and ')}`);
  if (access.error || !sent.length) {
    toast.warning(`HELPConnect access wasn't fully delivered${access.error ? `: ${access.error}` : ''}. Use the HELPConnect Access button to resend.`, { duration: 8000 });
  }
};

const SnapEtProgramReportCard = ({ caseId, caseData, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [wixData, setWixData] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [reportCardLastActivity, setReportCardLastActivity] = useState(null);
  const [inactivity, setInactivity] = useState(null);
  const [removedCourses, setRemovedCourses] = useState([]);
  const [accessRefresh, setAccessRefresh] = useState(0);
  const [isSnapEt, setIsSnapEt] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [participantProgram, setParticipantProgram] = useState('');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    selected_course_id: '',
    wix_enrollment_status: '',
    wix_participant_id: '',
    wix_member_id: '',
    wix_enrollment_date: ''
  });
  
  // Advance module state
  const [advanceModuleLoading, setAdvanceModuleLoading] = useState(false);

  // Second course enrollment state
  const [addSecondCourseOpen, setAddSecondCourseOpen] = useState(false);
  const [secondCourseOptions, setSecondCourseOptions] = useState([]);
  const [selectedSecondCourse, setSelectedSecondCourse] = useState('');
  const [selectedStartingModule, setSelectedStartingModule] = useState('1');
  const [addingSecondCourse, setAddingSecondCourse] = useState(false);

  // Check if this is a SNAP E&T case
  const checkIsSnapEtCase = () => {
    const programName = caseData?.program_name?.toLowerCase() || '';
    return programName.includes('snap') && programName.includes('e&t');
  };

  // Determine the primary course to display from multiple enrollments
  // Priority: 1. Active (not completed), 2. Most recently completed, 3. First enrollment
  const getPrimaryCourse = (data) => {
    if (!data) return null;
    
    const enrollments = (data.course_enrollments || []).filter((e) => !['removed', 'withdrawn'].includes(e.enrollment_status));
    
    // If no enrollments array or single/no enrollment, use top-level wixData as-is
    if (enrollments.length === 0) {
      return null; // Will fall back to wixData top-level fields
    }
    
    if (enrollments.length === 1) {
      return enrollments[0];
    }
    
    // Priority 1: Find active courses (not completed)
    const activeCourses = enrollments.filter(e => 
      e.progress_status !== 'completed' && e.enrollment_status === 'enrolled'
    );
    
    if (activeCourses.length > 0) {
      // If multiple active, prefer the one with most recent activity or enrollment
      return activeCourses.sort((a, b) => {
        const dateA = new Date(a.last_activity || a.enrollment_date || 0);
        const dateB = new Date(b.last_activity || b.enrollment_date || 0);
        return dateB - dateA; // Most recent first
      })[0];
    }
    
    // Priority 2: All completed - pick the most recently completed
    const completedCourses = enrollments.filter(e => e.progress_status === 'completed');
    if (completedCourses.length > 0) {
      return completedCourses.sort((a, b) => {
        const dateA = new Date(a.completion_date || a.last_activity || 0);
        const dateB = new Date(b.completion_date || b.last_activity || 0);
        return dateB - dateA; // Most recent first
      })[0];
    }
    
    // Priority 3: Fallback to first enrollment
    return enrollments[0];
  };

  useEffect(() => {
    const snapEt = checkIsSnapEtCase();
    setIsSnapEt(snapEt);
    if (snapEt) {
      fetchWixData();
      fetchCourseOptions();
    } else {
      setLoading(false);
    }
  }, [caseId, caseData]);

  const fetchWixData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.get(`${API}/cases/${caseId}/wix-program`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWixData(response.data.wix_program_data);
      setOrientation(response.data.orientation || null);
      setReportCardLastActivity(response.data.report_card_last_activity || null);
      setInactivity(response.data.inactivity || null);
      setRemovedCourses(response.data.removed_courses || []);
      setAccessRefresh((n) => n + 1);
      setIsSnapEt(response.data.is_snap_et);
    } catch (error) {
      console.error('Error fetching HELPConnect program data:', error);
      toast.error('Failed to load HELPConnect program data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseOptions = async () => {
    try {
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.get(`${API}/cases/${caseId}/wix-program/course-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseOptions(response.data.options || []);
      setParticipantProgram(response.data.participant_program || '');
    } catch (error) {
      console.error('Error fetching course options:', error);
    }
  };

  const handleEnroll = async () => {
    if (!selectedCourse) {
      toast.error('Please select a course or module first');
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.post(`${API}/cases/${caseId}/wix-program/enroll-v2`, 
        { course_or_module_id: selectedCourse },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      showAccessResult(response.data.access);
      
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error enrolling in HELPConnect:', error);
      const errorMsg = error.response?.data?.detail || 'HELPConnect enrollment failed. Please try again.';
      toast.error(errorMsg, { duration: 8000 });
      await fetchWixData(); // Refresh to show failed status
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.post(`${API}/cases/${caseId}/wix-program/sync-v2`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Progress synced successfully');
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error syncing HELPConnect progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync HELPConnect progress');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryEnrollment = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.post(`${API}/cases/${caseId}/wix-program/retry-enrollment-v2`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'HELPConnect enrollment completed');
      showAccessResult(response.data.access);
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error retrying HELPConnect enrollment:', error);
      toast.error(error.response?.data?.detail || 'HELPConnect enrollment failed again. Please try again.', { duration: 8000 });
      await fetchWixData();
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.get(`${API}/cases/${caseId}/wix-program/certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info('Certificate download pending implementation');
      // When implemented, this will trigger the actual download
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    } finally {
      setActionLoading(false);
    }
  };

  // Open edit modal with current values
  const handleOpenEditModal = () => {
    // Format date for input[type="datetime-local"]
    let formattedDate = '';
    if (wixData?.wix_enrollment_date) {
      try {
        const date = new Date(wixData.wix_enrollment_date);
        formattedDate = date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
      } catch (e) {
        formattedDate = '';
      }
    }
    
    setEditForm({
      selected_course_id: wixData?.selected_course_id || '',
      wix_enrollment_status: wixData?.wix_enrollment_status || 'not_enrolled',
      wix_enrollment_date: formattedDate
    });
    setEditModalOpen(true);
  };

  // Handle edit form field changes
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Save edit changes
  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('myhelp_token');
      
      // Build update payload with only changed/non-empty fields
      const payload = {};
      
      if (editForm.selected_course_id) {
        payload.selected_course_id = editForm.selected_course_id;
      }
      if (editForm.wix_enrollment_status) {
        payload.wix_enrollment_status = editForm.wix_enrollment_status;
      }
      if (editForm.wix_enrollment_date) {
        // Convert datetime-local format to ISO string
        payload.wix_enrollment_date = new Date(editForm.wix_enrollment_date).toISOString();
      }
      
      await axios.patch(`${API}/cases/${caseId}/wix-program`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Report Card updated successfully');
      setEditModalOpen(false);
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating HELPConnect program data:', error);
      toast.error(error.response?.data?.detail || 'Failed to update Report Card');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle manual module advancement for Pathways participants
  const handleAdvanceModule = async () => {
    try {
      setAdvanceModuleLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.post(`${API}/cases/${caseId}/wix-program/advance-module`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.all_modules_complete) {
        toast.success(response.data.message, { duration: 6000 });
      } else {
        toast.success(response.data.message);
        if (response.data.login_email_status === 'sent') {
          toast.info('HELPConnect access sent for the new module');
        }
      }
      
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error advancing module:', error);
      toast.error(error.response?.data?.detail || 'Failed to advance to next module');
    } finally {
      setAdvanceModuleLoading(false);
    }
  };

  // Open dialog to add a second course
  const handleOpenAddSecondCourse = () => {
    // Filter out courses that are already enrolled
    const existingEnrollments = wixData?.course_enrollments || [];
    const existingTypes = existingEnrollments.map(e => e.program_type);
    const hasPathways = existingTypes.includes('pathways_to_employability') || wixData?.selected_course_type === 'pathways_module';
    const hasHelpInstitute = existingTypes.includes('help_institute') || wixData?.selected_course_type === 'help_institute_course';
    
    // Build available options
    const options = [];
    
    // Add Pathways option if not already enrolled
    if (!hasPathways) {
      options.push({
        id: 'pathways_to_employability',
        name: 'Pathways to Employability',
        type: 'pathways',
        description: 'Select a starting module'
      });
    }
    
    // Add HELP Institute options if not already enrolled
    if (!hasHelpInstitute) {
      // We'll fetch the available courses from the courseOptions
      const helpInstituteCourses = courseOptions.filter(opt => opt.type === 'course');
      helpInstituteCourses.forEach(course => {
        options.push({
          id: course.id,
          name: course.name.replace('[HELP Institute] ', ''),
          type: 'help_institute',
          wix_program_id: course.wix_program_id
        });
      });
    }
    
    setSecondCourseOptions(options);
    setSelectedSecondCourse('');
    setSelectedStartingModule('1');
    setAddSecondCourseOpen(true);
  };

  // Handle adding a second course
  const handleAddSecondCourse = async () => {
    if (!selectedSecondCourse) {
      toast.error('Please select a course');
      return;
    }
    
    try {
      setAddingSecondCourse(true);
      const token = localStorage.getItem('myhelp_token');
      
      // Determine the course_or_module_id based on selection
      let courseOrModuleId = selectedSecondCourse;
      let startingModule = null;
      
      if (selectedSecondCourse === 'pathways_to_employability') {
        // For Pathways, use the module format and the selected starting module
        courseOrModuleId = `pathways_module_${selectedStartingModule}`;
        startingModule = parseInt(selectedStartingModule);
      }
      
      const response = await axios.post(
        `${API}/cases/${caseId}/wix-program/add-second-course`,
        {
          course_or_module_id: courseOrModuleId,
          starting_module: startingModule
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      showAccessResult(response.data.access);
      setAddSecondCourseOpen(false);
      await fetchWixData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding second course:', error);
      toast.error(error.response?.data?.detail || 'HELPConnect enrollment failed. The second course was not added.', { duration: 8000 });
    } finally {
      setAddingSecondCourse(false);
    }
  };

  // Don't render if not a SNAP E&T case
  if (!isSnapEt) {
    return null;
  }

  if (loading) {
    return (
      <Card className="card-shadow" data-testid="wix-program-report-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GraduationCap className="h-5 w-5 text-primary" />
            SNAP E&T Program Report Card
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Determine primary course for display when multiple enrollments exist
  const primaryCourse = getPrimaryCourse(wixData);
  const courseEnrollments = (wixData?.course_enrollments || []).filter((e) => !['removed', 'withdrawn'].includes(e.enrollment_status));
  const hasMultipleCourses = courseEnrollments.length > 1;
  
  // Use primary course data if available, otherwise fall back to top-level wixData
  const enrollmentStatus = primaryCourse?.enrollment_status || wixData?.wix_enrollment_status || 'not_enrolled';
  const progressStatus = primaryCourse?.progress_status || wixData?.progress_status;
  const certificateStatus = primaryCourse?.certificate_status || wixData?.certificate_status;
  const progressPercentage = primaryCourse?.progress_percentage ?? wixData?.progress_percentage;
  const completedSteps = primaryCourse?.completed_steps ?? wixData?.completed_steps;
  const totalSteps = primaryCourse?.total_steps ?? wixData?.total_steps;
  const primaryHcEnrollmentId = primaryCourse?.hc_enrollment_id || wixData?.hc_enrollment_id || primaryCourse?.wix_participant_id || wixData?.wix_participant_id;
  const hasHcEnrollment = !!primaryHcEnrollmentId;
  const isEnrolled = enrollmentStatus === 'enrolled';
  const isNotEnrolled = enrollmentStatus === 'not_enrolled';
  const isEnrollmentFailed = enrollmentStatus === 'enrollment_failed';
  const isRetrying = enrollmentStatus === 'retrying';
  const canDownloadCertificate = certificateStatus === 'available' || certificateStatus === 'downloaded';
  const selectedCourseName = primaryCourse?.course_name || wixData?.selected_course_name;
  const enrollmentError = primaryCourse?.enrollment_error || wixData?.enrollment_error;
  const canAddSecondCourse = (wixData?.wix_enrollment_status === 'enrolled' || isEnrolled) && !hasMultipleCourses;
  
  // Additional primary course details for display
  const primaryEnrollmentDate = primaryCourse?.enrollment_date || wixData?.wix_enrollment_date;
  const primaryCompletionDate = primaryCourse?.completion_date || wixData?.completion_date;

  // Get badge styling
  const getEnrollmentBadge = () => {
    const style = ENROLLMENT_STATUS_STYLES[enrollmentStatus] || ENROLLMENT_STATUS_STYLES.not_enrolled;
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  const getProgressBadge = () => {
    if (!progressStatus) return null;
    // Normalize progressStatus to lowercase for matching
    const normalizedStatus = progressStatus.toLowerCase();
    const style = PROGRESS_STATUS_STYLES[normalizedStatus] || PROGRESS_STATUS_STYLES.not_started;
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  // Check if any courses are linked to HELPConnect content
  const hasConfiguredCourses = courseOptions.some(opt => opt.wix_program_id);

  // Empty state - not enrolled
  if (isNotEnrolled && !wixData?.enrollment_attempted) {
    return (
      <Card className="card-shadow" data-testid="wix-program-report-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GraduationCap className="h-5 w-5 text-primary" />
            SNAP E&T Program Report Card
          </CardTitle>
          <CardDescription>
            HELPConnect enrollment, progress, and certificate tracking
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">HELPConnect Enrollment:</span>
            {getEnrollmentBadge()}
          </div>
          {removedCourses.length > 0 && (
            <RestoreCourseButton caseId={caseId} removed={removedCourses} participantName={caseData?.participant_name} onRestored={fetchWixData} />
          )}

          {/* Participant's Program */}
          {participantProgram && (
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Program:</span>
                <span className="text-sm">{participantProgram}</span>
              </div>
            </div>
          )}
          
          {/* Course/Module Selection */}
          {courseOptions.length > 0 ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Course/Module:</label>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger className="w-full" data-testid="course-select">
                  <SelectValue placeholder="Select a course or module..." />
                </SelectTrigger>
                <SelectContent>
                  {courseOptions.map((option) => (
                    <SelectItem 
                      key={option.id} 
                      value={option.id}
                      disabled={!option.wix_program_id}
                    >
                      <div className="flex items-center gap-2">
                        <span>{option.name}</span>
                        {!option.wix_program_id && (
                          <span className="text-xs text-muted-foreground">(Not Configured)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!hasConfiguredCourses && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  No courses are linked to HELPConnect yet. Ask a System Administrator to finish the course setup in Administration.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No courses available. Please check the participant's program in Targeted Outcomes.
            </p>
          )}
          
          <div className="pt-2">
            <Button 
              onClick={handleEnroll} 
              disabled={actionLoading || !selectedCourse || !hasConfiguredCourses}
              className="w-full sm:w-auto"
              data-testid="enroll-in-program-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Enroll in HELPConnect
            </Button>
            <p className="text-xs text-muted-foreground mt-2">The participant's HELPConnect access link is sent by text and email right after enrolling.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-shadow" data-testid="wix-program-report-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <GraduationCap className="h-5 w-5 text-primary" />
          SNAP E&T Program Report Card
          {inactivity && (
            <Badge className="ml-auto bg-red-100 text-red-700 border border-red-200 hover:bg-red-100" data-testid="report-card-inactivity-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              No activity in {inactivity.days} days
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          HELPConnect enrollment, progress, and certificate tracking
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Enrolled Course Name */}
        {selectedCourseName && (
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Course:</span>
              <span className="text-sm">{selectedCourseName}</span>
            </div>
          </div>
        )}

        {/* Enrollment and Status Row */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">HELPConnect Enrollment:</span>
            {getEnrollmentBadge()}
          </div>
          {progressStatus && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Status:</span>
              {getProgressBadge()}
            </div>
          )}
          {orientation?.status && PROGRESS_STATUS_STYLES[orientation.status] && (
            <div className="flex items-center gap-2" data-testid="report-card-orientation-status">
              <span className="text-sm font-medium text-muted-foreground">Orientation:</span>
              <Badge className={PROGRESS_STATUS_STYLES[orientation.status].className}>{PROGRESS_STATUS_STYLES[orientation.status].label}</Badge>
            </div>
          )}
        </div>

        {/* Enrollment Error */}
        {isEnrollmentFailed && enrollmentError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400">Enrollment Failed</p>
                <p className="text-xs text-red-600 dark:text-red-500 mt-1">{enrollmentError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Retry in Progress */}
        {isRetrying && (
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-purple-700 dark:text-purple-400">Retry in Progress</p>
                <p className="text-xs text-purple-600 dark:text-purple-500 mt-1">
                  Enrolling the participant in HELPConnect...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Section - Only show if enrolled and has progress data */}
        {isEnrolled && progressPercentage !== null && progressPercentage !== undefined && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Progress</span>
              <span className="text-sm font-semibold">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3" data-testid="progress-bar" />
            {completedSteps !== null && totalSteps !== null && (
              <p className="text-sm text-muted-foreground">
                {completedSteps} of {totalSteps} Steps Completed
              </p>
            )}
          </div>
        )}

        {/* Dates Section - Only show relevant data */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {primaryEnrollmentDate && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Enrolled:</span>
              <span>{formatDateTime(primaryEnrollmentDate)}</span>
            </div>
          )}
          <div className="flex items-center gap-2" data-testid="report-card-last-activity">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Last Activity:</span>
            <span>{reportCardLastActivity ? formatDateTime(reportCardLastActivity) : 'No activity yet'}</span>
          </div>
          {wixData?.last_synced && (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Last Synced:</span>
              <span>{formatDateTime(wixData.last_synced)}</span>
            </div>
          )}
          {primaryCompletionDate && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-muted-foreground">Completed:</span>
              <span className="text-green-600 font-medium">{formatDateTime(primaryCompletionDate)}</span>
            </div>
          )}
        </div>

        {/* Certificate Section - Only show if relevant */}
        {certificateStatus && certificateStatus !== 'not_eligible' && (
          <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
            <Award className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">Certificate:</span>
            <Badge className={CERTIFICATE_STATUS_STYLES[certificateStatus]?.className || ''}>
              {CERTIFICATE_STATUS_STYLES[certificateStatus]?.label || certificateStatus}
            </Badge>
          </div>
        )}

        {/* Multiple Course Enrollments Section */}
        {hasMultipleCourses && courseEnrollments.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              All Course Enrollments ({courseEnrollments.length})
            </h4>
            <div className="space-y-3">
              {courseEnrollments.map((enrollment, index) => {
                // Check if this is the primary course (currently displayed at top)
                const isPrimaryDisplay = primaryCourse && 
                  (enrollment.enrollment_id === primaryCourse.enrollment_id || 
                   (enrollment.course_name === primaryCourse.course_name && 
                    (enrollment.hc_enrollment_id || enrollment.wix_participant_id) === (primaryCourse.hc_enrollment_id || primaryCourse.wix_participant_id)));
                
                return (
                  <div 
                    key={enrollment.enrollment_id || index}
                    className={`p-3 rounded-lg border ${
                      isPrimaryDisplay 
                        ? 'bg-primary/5 border-primary/30' 
                        : 'bg-muted/30 border-border/50'
                    }`}
                    data-testid={`course-enrollment-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{enrollment.course_name}</span>
                          {isPrimaryDisplay && (
                            <Badge variant="default" className="text-xs bg-primary/80">Active Display</Badge>
                          )}
                          {enrollment.is_primary && !isPrimaryDisplay && (
                            <Badge variant="outline" className="text-xs">Primary</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {(enrollment.hc_enrollment_id || enrollment.wix_participant_id) && (
                            <span>HELPConnect Enrollment ID: {enrollment.hc_enrollment_id || enrollment.wix_participant_id}</span>
                          )}
                          {enrollment.enrollment_date && (
                            <span>Enrolled: {new Date(enrollment.enrollment_date).toLocaleDateString()}</span>
                          )}
                        </div>
                        {/* Pathways module info */}
                        {enrollment.program_type === 'pathways_to_employability' && (
                          <div className="mt-2 text-xs">
                            <span className="text-muted-foreground">
                              Starting Module: {enrollment.starting_module} | Current: Module {enrollment.current_module}
                            </span>
                            {enrollment.module_history?.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {enrollment.module_history.map((mh, mhIdx) => (
                                  <Badge 
                                    key={mhIdx}
                                    variant="outline" 
                                    className={`text-xs ${mh.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                                  >
                                    M{mh.module_number}: {mh.status}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge 
                          className={
                            enrollment.progress_status === 'completed' ? 'bg-green-100 text-green-700' :
                            enrollment.enrollment_status === 'enrolled' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }
                        >
                          {enrollment.progress_status === 'completed' ? 'Completed' : 
                           enrollment.enrollment_status === 'enrolled' ? 'In Progress' : 
                           enrollment.enrollment_status}
                        </Badge>
                        {enrollment.progress_percentage !== undefined && enrollment.progress_percentage !== null && (
                          <span className="text-xs font-medium">{enrollment.progress_percentage}% complete</span>
                        )}
                      </div>
                    </div>
                    {/* Mini progress bar for each course */}
                    {enrollment.progress_percentage !== undefined && enrollment.progress_percentage !== null && (
                      <Progress value={enrollment.progress_percentage} className="h-1.5 mt-2" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasHcEnrollment && <HelpConnectAccessStatus participantId={caseData?.participant_id} refreshKey={accessRefresh} />}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {/* Enroll in Program - when not enrolled */}
          {isNotEnrolled && (
            <div className="flex flex-col gap-2 w-full">
              {courseOptions.length > 0 && (
                <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                  <SelectTrigger className="w-full sm:w-64" data-testid="course-select">
                    <SelectValue placeholder="Select a course or module..." />
                  </SelectTrigger>
                  <SelectContent>
                    {courseOptions.map((option) => (
                      <SelectItem 
                        key={option.id} 
                        value={option.id}
                        disabled={!option.wix_program_id}
                      >
                        <div className="flex items-center gap-2">
                          <span>{option.name}</span>
                          {!option.wix_program_id && (
                            <span className="text-xs text-muted-foreground">(Not Configured)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button 
                onClick={handleEnroll} 
                disabled={actionLoading || !selectedCourse}
                size="sm"
                data-testid="enroll-in-program-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                Enroll in HELPConnect
              </Button>
            </div>
          )}

          {/* Retry HELPConnect Enrollment - when enrollment failed or retrying */}
          {(isEnrollmentFailed || isRetrying) && (
            <Button 
              onClick={handleRetryEnrollment} 
              disabled={actionLoading || isRetrying}
              variant="outline"
              size="sm"
              className={isRetrying 
                ? "border-purple-300 text-purple-700 dark:border-purple-800 dark:text-purple-400" 
                : "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
              }
              data-testid="retry-enrollment-btn"
            >
              {(actionLoading || isRetrying) ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              {isRetrying ? 'Retrying...' : 'Retry HELPConnect Enrollment'}
            </Button>
          )}

          {/* Sync Progress - from HELPConnect */}
          {hasHcEnrollment && (
            <Button 
              onClick={handleSync} 
              disabled={actionLoading}
              variant="outline"
              size="sm"
              data-testid="sync-progress-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Progress
            </Button>
          )}

          {hasHcEnrollment && <HelpConnectAccessButton participantId={caseData?.participant_id} onSent={() => setAccessRefresh((n) => n + 1)} />}
          {hasHcEnrollment && <HelpConnectViewPortalButton participantId={caseData?.participant_id} />}
          {hasHcEnrollment && <HelpConnectCertificatesButton participantId={caseData?.participant_id} />}

          {/* Download Certificate - when available */}
          {canDownloadCertificate && (
            <Button 
              onClick={handleDownloadCertificate} 
              disabled={actionLoading}
              variant="outline"
              size="sm"
              className="border-green-300 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20"
              data-testid="download-certificate-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Certificate
            </Button>
          )}

          {/* Advance to Next Module - Only for Pathways participants who are enrolled */}
          {isEnrolled && wixData?.selected_course_type === 'pathways_module' && !wixData?.program_completed && (
            <Button 
              onClick={handleAdvanceModule} 
              disabled={advanceModuleLoading || actionLoading}
              variant="outline"
              size="sm"
              className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
              data-testid="advance-module-btn"
            >
              {advanceModuleLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FastForward className="h-4 w-4 mr-2" />
              )}
              Advance to Next Module
            </Button>
          )}

          {/* Add Second Course - when enrolled and doesn't have multiple courses yet */}
          {canAddSecondCourse && (
            <Button 
              onClick={handleOpenAddSecondCourse} 
              disabled={actionLoading}
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
              data-testid="add-second-course-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Second Course
            </Button>
          )}

          {/* Edit Report Card - always visible */}
          <Button 
            onClick={handleOpenEditModal} 
            disabled={actionLoading}
            variant="outline"
            size="sm"
            data-testid="edit-report-card-btn"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <RemoveCourseButton caseId={caseId} wixData={wixData} participantName={caseData?.participant_name} onRemoved={fetchWixData} />
          <RestoreCourseButton caseId={caseId} removed={removedCourses} participantName={caseData?.participant_name} onRestored={fetchWixData} />
        </div>

        {hasHcEnrollment && <StaffPathwaysTracker participantId={caseData?.participant_id} />}

        {/* Module History - Show previously completed modules */}
        {wixData?.module_history && wixData.module_history.length > 0 && (
          <div className="pt-3 border-t">
            <p className="text-sm font-medium mb-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Completed Modules ({wixData.module_history.length})
            </p>
            <div className="space-y-2">
              {[...wixData.module_history].sort((a, b) => (a.module_number || 0) - (b.module_number || 0)).map((mod, idx) => (
                <div key={idx} className="text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-green-700 dark:text-green-400">
                      Module {mod.module_number}: {mod.course_name}
                    </span>
                    {mod.progress_percentage !== undefined && (
                      <span className="text-green-600">{mod.progress_percentage}%</span>
                    )}
                  </div>
                  {mod.completed_at && (
                    <p className="text-green-600 dark:text-green-500 mt-1">
                      Completed: {formatDateTime(mod.completed_at)}
                      {mod.manual_advancement && <span className="ml-2">(Manual)</span>}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Program Completed Message */}
        {wixData?.program_completed && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-700 dark:text-green-400">
                All 8 Pathways to Employability Modules Completed!
              </span>
            </div>
            {wixData.program_completion_date && (
              <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                Program completed on {formatDateTime(wixData.program_completion_date)}
              </p>
            )}
          </div>
        )}

        {/* Technical IDs - Secondary text at bottom */}
        {primaryHcEnrollmentId && (
          <div className="pt-3 border-t text-xs text-muted-foreground space-y-1">
            <p data-testid="hc-enrollment-id">HELPConnect Enrollment ID: <span className="font-mono">{primaryHcEnrollmentId}</span></p>
          </div>
        )}

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-[500px]" data-testid="edit-report-card-dialog">
            <DialogHeader>
              <DialogTitle>Edit Report Card</DialogTitle>
              <DialogDescription>
                Update HELPConnect enrollment details for this participant. Changes will be saved immediately.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {/* Course Selection */}
              <div className="grid gap-2">
                <Label htmlFor="edit-course">Assigned Course</Label>
                <Select 
                  value={editForm.selected_course_id} 
                  onValueChange={(value) => handleEditFormChange('selected_course_id', value)}
                >
                  <SelectTrigger id="edit-course" data-testid="edit-course-select">
                    <SelectValue placeholder="Select a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {courseOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Enrollment Status */}
              <div className="grid gap-2">
                <Label htmlFor="edit-status">HELPConnect Enrollment Status</Label>
                <Select 
                  value={editForm.wix_enrollment_status} 
                  onValueChange={(value) => handleEditFormChange('wix_enrollment_status', value)}
                >
                  <SelectTrigger id="edit-status" data-testid="edit-status-select">
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enrolled">Enrolled</SelectItem>
                    <SelectItem value="not_enrolled">Not Enrolled</SelectItem>
                    <SelectItem value="enrollment_failed">Enrollment Failed</SelectItem>
                    <SelectItem value="retrying">Retry in Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Enrollment Date */}
              <div className="grid gap-2">
                <Label htmlFor="edit-enrollment-date">Enrollment Date</Label>
                <Input
                  id="edit-enrollment-date"
                  type="datetime-local"
                  value={editForm.wix_enrollment_date}
                  onChange={(e) => handleEditFormChange('wix_enrollment_date', e.target.value)}
                  data-testid="edit-enrollment-date-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditModalOpen(false)}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={actionLoading}
                data-testid="save-edit-btn"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Second Course Dialog */}
        <Dialog open={addSecondCourseOpen} onOpenChange={setAddSecondCourseOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-emerald-600" />
                Add Second Course
              </DialogTitle>
              <DialogDescription>
                Add an additional course to this participant's Report Card. The existing course enrollment will remain intact.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Course Selection */}
              <div className="space-y-2">
                <Label htmlFor="second-course">Select Course</Label>
                <Select 
                  value={selectedSecondCourse} 
                  onValueChange={setSelectedSecondCourse}
                >
                  <SelectTrigger id="second-course" data-testid="second-course-select">
                    <SelectValue placeholder="Choose a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {secondCourseOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{option.name}</span>
                          {option.description && (
                            <span className="text-xs text-muted-foreground">{option.description}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                    {secondCourseOptions.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No additional courses available. The participant is already enrolled in all available course types.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Starting Module Selection (for Pathways) */}
              {selectedSecondCourse === 'pathways_to_employability' && (
                <div className="space-y-2">
                  <Label htmlFor="starting-module">Starting Module</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Select which module the participant is starting from. They will advance through modules sequentially from this point.
                  </p>
                  <Select 
                    value={selectedStartingModule} 
                    onValueChange={setSelectedStartingModule}
                  >
                    <SelectTrigger id="starting-module" data-testid="starting-module-select">
                      <SelectValue placeholder="Select starting module..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <SelectItem key={num} value={String(num)}>
                          Module {num}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Note: Module advancement follows the Monday auto-advancement logic. When the current module is marked Complete in HELPConnect, the participant will advance to the next module on Monday.
                  </p>
                </div>
              )}

              {/* Current Enrollment Info */}
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium mb-1">Current Enrollment</p>
                <p className="text-muted-foreground">
                  {selectedCourseName || 'Unknown Course'} - {enrollmentStatus === 'enrolled' ? 'Active' : enrollmentStatus}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setAddSecondCourseOpen(false)}
                disabled={addingSecondCourse}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddSecondCourse}
                disabled={addingSecondCourse || !selectedSecondCourse || secondCourseOptions.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-add-second-course-btn"
              >
                {addingSecondCourse ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Course
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default SnapEtProgramReportCard;
